import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Col, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";

const DateRange = (props: {
  startDate?: string,
  endDate?: string,
  minStartDate?: string,
  maxEndDate?: string,
  requireStartDate?: boolean,
  requireEndDate?: boolean,
  onStartDateChange: (date: string) => void,
  onEndDateChange: (date: string) => void,
  onValidate?: (valid: boolean) => void
}) => {
  const { startDate, endDate,
    minStartDate, maxEndDate,
    requireStartDate, requireEndDate,
    onStartDateChange, onEndDateChange,
    onValidate } = props;

  const [startDateError, setStartDateError] = useState<string>();
  const [endDateError, setEndDateError] = useState<string>();

  const isValid = (start?: string, end?: string) => {
    if (!start && !requireStartDate) return true;
    if (!end && !requireEndDate) return true;
    return !!start && !!end && new Date(start) <= new Date(end);
  }

  const handleStartDate = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value: start } = event.target;
    onStartDateChange(start);

    if (!isValid(start, endDate)) {
      setStartDateError(start ? 'Start date must be earlier than the end date.' : 'Start date is mandatory.');
    }
  }, [endDate]);

  const handleEndDate = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value: end } = event.target;
    onEndDateChange(end);

    if (!isValid(startDate, end)) {
      setEndDateError(end ? 'End date must be later than the start date.' : 'End date is mandatory.');
    }
  }, [startDate]);

  useEffect(() => {
    const ok = isValid(startDate, endDate);
    if (ok) {
      setStartDateError(undefined);
      setEndDateError(undefined);
    }

    onValidate?.(ok);
  }, [startDate, endDate]);

  return (
    <Row>
      <Col md={6}>
        <section aria-label="Start date">
          <FormGroup row>
            <Label for="start_date" sm={4}>Start Date:</Label>
            <Col sm={6}>
              <Input id="start_date" name="date" type="date"
                value={startDate} min={minStartDate} max={endDate}
                onChange={handleStartDate}
                invalid={!!startDateError} />
              <FormFeedback>{startDateError}</FormFeedback>
            </Col>
          </FormGroup>
        </section>
      </Col>
      <Col md={6}>
        <section aria-label="End date">
          <FormGroup row>
            <Label for="end_date" sm={4}>End Date:</Label>
            <Col sm={6}>
              <Input id="end_date" name="date" type="date"
                value={endDate} min={startDate} max={maxEndDate}
                onChange={handleEndDate}
                invalid={!!endDateError} />
              <FormFeedback>{endDateError}</FormFeedback>
            </Col>
          </FormGroup>
        </section>
      </Col>
    </Row>
  );
};

export default DateRange;
